import { assistantIsOpen } from "../../lib/compare";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, FileText, Minus, Plus } from "lucide-react";
import { money, canBuy, addToQuote, stockInfo } from "./BuyBox";
import { StockPill } from "../StockPill";
import { useCustomer } from "../CustomerContext";

const PHONE = "(max-width: 640px)";
export const usePhone = () => {
  const [on, setOn] = useState(() => window.matchMedia(PHONE).matches);
  useEffect(() => {
    const m = window.matchMedia(PHONE), f = () => setOn(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return on;
};

// T50: phone-only (<= 640px); larger views neither render nor observe it.
export const StickyBuyBar = (props) => (usePhone() ? <Bar {...props} /> : null);

const MAX_LINE_QTY = 999;  // same ceiling as the cart

// T51: compact stepper sharing the buy box's quantity state (two-way), capped at available (stock on hand - reservations).
const Stepper = ({ qty, setQty, max, tab }) => {
  const [draft, setDraft] = useState(null);
  const set = (v) => setQty(Math.min(max, Math.max(1, v)));
  const b = "flex h-11 w-11 items-center justify-center disabled:opacity-40";
  return (
    <div className="flex shrink-0 items-center rounded-full border border-slate-300 bg-white" role="group" aria-label="Quantity" data-testid="sticky-qty">
      <button type="button" tabIndex={tab} aria-label="Decrease quantity" data-testid="sticky-qty-dec" disabled={qty <= 1} onClick={() => set(qty - 1)} className={`${b} rounded-l-full`}><Minus size={16} aria-hidden="true" /></button>
      <input type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="off" tabIndex={tab} aria-label="Quantity" data-testid="sticky-qty-input"
        value={draft ?? String(qty)} className="h-11 w-10 bg-transparent text-center font-semibold tabular-nums outline-none"
        onChange={(e) => { const d = e.target.value.replace(/\D/g, "").slice(0, 3); setDraft(d && Number(d) > max ? String(max) : d); if (d && Number(d) > 0) set(Number(d)); }}
        onBlur={() => setDraft(null)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />
      <button type="button" tabIndex={tab} aria-label="Increase quantity" data-testid="sticky-qty-inc" disabled={qty >= max} onClick={() => set(qty + 1)} className={`${b} rounded-r-full`}><Plus size={16} aria-hidden="true" /></button>
    </div>
  );
};

const Bar = ({ p, target, qty, setQty, onAdd, adding }) => {
  const [raw, setShow] = useState(false);
  const [asst, setAsst] = useState(assistantIsOpen());
  useEffect(() => { const f = () => setAsst(assistantIsOpen()); window.addEventListener("assistant-state", f); return () => window.removeEventListener("assistant-state", f); }, []);
  const show = raw && !asst;  // T74: the Rhyte AI sheet takes the bottom of the screen while it is open
  const nav = useNavigate();
  const { customer } = useCustomer();
  useEffect(() => {
    const el = target;
    if (!el) return undefined;
    // Visible exactly when the buy box is scrolled out of view above: checked on mount, scroll, resize and layout crossings.
    let raf = 0;
    const check = () => { raf = 0; setShow(el.getBoundingClientRect().bottom < 0); };
    const queue = () => { if (!raf) raf = requestAnimationFrame(check); };
    check();
    window.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue);
    const io = new IntersectionObserver(queue);
    io.observe(el);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("scroll", queue); window.removeEventListener("resize", queue); io.disconnect(); };
  }, [target]);
  const box = useRef(null);
  useEffect(() => {  // T63: the phone compare bar sits just above this bar while it's showing
    const set = () => document.documentElement.style.setProperty("--rs-buybar-h", show && box.current ? `${box.current.offsetHeight}px` : "0px");
    set();
    const ro = new ResizeObserver(set);
    if (box.current) ro.observe(box.current);
    return () => { ro.disconnect(); document.documentElement.style.setProperty("--rs-buybar-h", "0px"); };
  }, [show]);
  const buy = canBuy(p);
  const held = p.availability_state === "reserved" || p.availability_state === "unavailable_order";
  const cap = p.available_qty ?? p.stock_qty ?? null;  // T51b: server figure; stock_qty is the safety net
  const max = Math.max(1, Math.min(cap ?? MAX_LINE_QTY, MAX_LINE_QTY));
  const tab = show ? 0 : -1;
  const st = stockInfo(p);
  return (
    <div ref={box} aria-hidden={!show} data-testid="sticky-buy-bar" data-visible={show}
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 shadow-[0_-6px_20px_rgba(15,23,42,0.08)] backdrop-blur transition-transform duration-200 motion-reduce:transition-none ${show ? "translate-y-0" : "pointer-events-none translate-y-full"}`}>
      <div className="mx-auto max-w-7xl px-4 py-2 sm:px-6">
        <div className="flex min-w-0 items-baseline gap-2">
          <div className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900" data-testid="sticky-buy-name">{p.display_name}</div>
          {Number(p.price_inc_gst) > 0 && <div className="shrink-0 text-sm font-bold tabular-nums text-[var(--brand-primary)]" data-testid="sticky-buy-price">{money(p.price_inc_gst)} <span className="text-xs font-medium text-slate-500">inc GST</span></div>}
        </div>
        {held ? (
          <div className="mt-1.5 flex min-h-[44px] items-center" data-testid="sticky-status">
            <StockPill p={p} testid="sticky-stock-pill" />
          </div>
        ) : buy ? (
          <>
            <div className="mt-1.5 flex items-center gap-2">
              <Stepper qty={qty} setQty={setQty} max={max} tab={tab} />
              <button type="button" tabIndex={tab} onClick={onAdd} disabled={adding} data-testid="sticky-add-to-cart" aria-label={`Add ${qty} to cart`}
                className="flex h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-[var(--brand-button)] px-4 text-sm font-bold text-[var(--brand-on-button)] hover:bg-[var(--brand-button-hover)] disabled:opacity-50">
                <ShoppingCart size={16} aria-hidden="true" /> <span className="truncate">{adding ? "Adding…" : "Add to cart"}</span>
              </button>
            </div>
            <p className="mt-1 text-xs font-medium text-amber-700" aria-live="polite" data-testid="sticky-qty-cap">{cap != null && qty >= max ? `Only ${max} available` : ""}</p>
          </>
        ) : (
          <button type="button" tabIndex={tab} onClick={() => addToQuote(p, qty, nav, customer)} data-testid="sticky-request-quote"
            className="mt-1.5 flex h-11 w-full items-center justify-center gap-2 rounded-full border border-[var(--brand-primary)] px-5 text-sm font-semibold text-[var(--brand-primary)]">
            <FileText size={16} aria-hidden="true" /> Request a quote
          </button>
        )}
      </div>
    </div>
  );
};
